/**
 * Administration > Instances (M2 W5b). Lists registry instances (optionally filtered by
 * tenant), creates them, and edits label/status/connection-secret-reference. The
 * "Connection secret reference" is a POINTER (a secret-manager key / env-var name), NEVER
 * a secret value, so it is a plain text field with an explicit caption, not a password
 * input. Mutations refetch (no optimistic state). Non-PHI registry data.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import {
  SkyButton,
  SkyDataGrid,
  SkyDialog,
  SkyDropdown,
  SkyInput,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { ApiProblem } from "../../lib/api";
import {
  listInstances,
  listTenants,
  createInstance,
  updateInstance,
  type AdminInstance,
  type AdminTenant,
} from "../../lib/adminApi";
import { usePermission } from "../../lib/session";
import { AdminPage, AdminError, StatusBadge } from "./adminShared";

const STATUS_OPTIONS = [
  { value: "active", text: "Active" },
  { value: "provisioning", text: "Provisioning" },
  { value: "disabled", text: "Disabled" },
];
const ENV_OPTIONS = [
  { value: "dev", text: "Development" },
  { value: "prod", text: "Production" },
];
const SECRET_REF_HINT =
  "A POINTER to a secret (a secret-manager key or env-var name). Never paste a secret value here.";

function problemTitle(e: unknown, fallback: string): string {
  return e instanceof ApiProblem ? e.problem.title : fallback;
}

export function AdminInstances() {
  const { canManage } = usePermission();
  const canEdit = canManage("Instances");

  const [instances, setInstances] = useState<AdminInstance[]>([]);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [tenantFilter, setTenantFilter] = useState<string>("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminInstance | null>(null);

  // Generation counter so a tenant-filter change cannot leave a stale instance list on
  // screen: each load is stamped and only the newest one applies its result.
  const loadGenRef = useRef(0);

  const load = useCallback(() => {
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    listInstances(tenantFilter === "all" ? undefined : tenantFilter)
      .then(({ data }) => {
        if (gen !== loadGenRef.current) return;
        setInstances(data);
      })
      .catch((e: unknown) => {
        if (gen !== loadGenRef.current) return;
        setError(problemTitle(e, "Failed to load instances."));
        setInstances([]);
      })
      .finally(() => {
        if (gen === loadGenRef.current) setLoading(false);
      });
  }, [tenantFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Tenants drive the filter dropdown + the create form (best-effort; degrade if forbidden).
  // The cancelled flag drops a late resolution after unmount so it never sets state on a
  // torn-down component.
  useEffect(() => {
    let cancelled = false;
    listTenants()
      .then(({ data }) => {
        if (!cancelled) setTenants(data);
      })
      .catch(() => {
        if (!cancelled) setTenants([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tenantName = (id: string) =>
    tenants.find((t) => t.id === id)?.displayName ?? id;

  const columns: SkyDataGridColumn<AdminInstance>[] = [
    { key: "label", header: "Label", width: "20%" },
    { key: "name", header: "Name", width: "14%" },
    {
      key: "tenantId",
      header: "Tenant",
      width: "18%",
      renderCell: (_v, row) => tenantName(row.tenantId),
    },
    { key: "environment", header: "Environment", width: "12%" },
    {
      key: "connectionSecretRef",
      header: "Secret pointer",
      width: "18%",
      renderCell: (_v, row) =>
        row.connectionSecretRef ?? <SkyText size={200}>Not set</SkyText>,
    },
    {
      key: "status",
      header: "Status",
      width: "10%",
      renderCell: (_v, row) => <StatusBadge status={row.status} />,
    },
    {
      key: "actions",
      header: "Actions",
      width: "10%",
      renderCell: (_v, row) => (
        <SkyButton
          appearance="outline"
          size="small"
          icon={Pencil}
          disabled={!canEdit}
          onClick={() => setEditing(row)}
        >
          Edit
        </SkyButton>
      ),
    },
  ];

  return (
    <AdminPage
      crumbs={[
        { key: "home", label: "skyCare", to: "/" },
        { key: "admin", label: "Administration" },
        { key: "instances", label: "Instances" },
      ]}
      title="Instances"
      description="Per-tenant database instances. Connection secrets are referenced by pointer, never stored here."
      requires={["manageInstances"]}
      headerAction={
        canEdit ? (
          <SkyButton
            appearance="primary"
            icon={Plus}
            disabled={tenants.length === 0}
            onClick={() => setCreateOpen(true)}
          >
            New instance
          </SkyButton>
        ) : undefined
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64 max-w-full">
          <SkyDropdown
            label="Filter by tenant"
            options={[
              { value: "all", text: "All tenants" },
              ...tenants.map((t) => ({ value: t.id, text: t.displayName })),
            ]}
            selectedOptions={[tenantFilter]}
            onOptionSelect={(vals) => setTenantFilter(vals[0] ?? "all")}
          />
        </div>
      </div>

      {error && <AdminError title="Could not complete the request" message={error} />}

      <SkyDataGrid<AdminInstance>
        data={instances}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        emptyMessage="No instances found"
      />

      {createOpen && (
        <CreateInstanceDialog
          tenants={tenants}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}
      {editing && (
        <EditInstanceDialog
          instance={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </AdminPage>
  );
}

function CreateInstanceDialog({
  tenants,
  onClose,
  onCreated,
}: {
  tenants: AdminTenant[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id ?? "");
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [environment, setEnvironment] = useState<"dev" | "prod">("dev");
  const [dbHost, setDbHost] = useState("");
  const [dbName, setDbName] = useState("");
  const [dbUser, setDbUser] = useState("");
  const [lakebaseProjectId, setLakebaseProjectId] = useState("");
  const [lakebaseBranch, setLakebaseBranch] = useState("");
  const [lakebaseEndpoint, setLakebaseEndpoint] = useState("");
  const [connectionSecretRef, setConnectionSecretRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid =
    tenantId !== "" &&
    name.trim() &&
    label.trim() &&
    dbHost.trim() &&
    dbName.trim() &&
    dbUser.trim() &&
    lakebaseProjectId.trim() &&
    lakebaseBranch.trim() &&
    lakebaseEndpoint.trim();

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await createInstance({
        tenantId,
        name: name.trim(),
        label: label.trim(),
        environment,
        dbHost: dbHost.trim(),
        dbName: dbName.trim(),
        dbUser: dbUser.trim(),
        lakebaseProjectId: lakebaseProjectId.trim(),
        lakebaseBranch: lakebaseBranch.trim(),
        lakebaseEndpoint: lakebaseEndpoint.trim(),
        connectionSecretRef: connectionSecretRef.trim() || null,
      });
      onCreated();
    } catch (e: unknown) {
      setErr(problemTitle(e, "Failed to create instance."));
      setBusy(false);
    }
  }

  return (
    <SkyDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Create instance"
      size="lg"
      primaryAction={{ label: "Create instance", onClick: submit, loading: busy, disabled: !valid || busy }}
      secondaryAction={{ label: "Cancel", onClick: onClose, disabled: busy }}
    >
      <div className="flex flex-col gap-3">
        {err && <AdminError title="Could not create instance" message={err} />}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <SkyDropdown
            label="Tenant"
            options={tenants.map((t) => ({ value: t.id, text: t.displayName }))}
            selectedOptions={tenantId ? [tenantId] : []}
            onOptionSelect={(vals) => setTenantId(vals[0] ?? "")}
            required
          />
          <SkyDropdown
            label="Environment"
            options={ENV_OPTIONS}
            selectedOptions={[environment]}
            onOptionSelect={(vals) => setEnvironment((vals[0] as "dev" | "prod") ?? "dev")}
          />
          <SkyInput label="Name" required value={name} onChange={(e) => setName(e.target.value)} />
          <SkyInput label="Label" required value={label} onChange={(e) => setLabel(e.target.value)} />
          <SkyInput label="DB host" required value={dbHost} onChange={(e) => setDbHost(e.target.value)} />
          <SkyInput label="DB name" required value={dbName} onChange={(e) => setDbName(e.target.value)} />
          <SkyInput label="DB user" required value={dbUser} onChange={(e) => setDbUser(e.target.value)} />
          <SkyInput
            label="Lakebase project id"
            required
            value={lakebaseProjectId}
            onChange={(e) => setLakebaseProjectId(e.target.value)}
          />
          <SkyInput
            label="Lakebase branch"
            required
            value={lakebaseBranch}
            onChange={(e) => setLakebaseBranch(e.target.value)}
          />
          <SkyInput
            label="Lakebase endpoint"
            required
            value={lakebaseEndpoint}
            onChange={(e) => setLakebaseEndpoint(e.target.value)}
          />
        </div>
        <SkyInput
          label="Connection secret reference"
          placeholder="e.g. LAKEBASE_SANDBOX_PASSWORD"
          hint={SECRET_REF_HINT}
          value={connectionSecretRef}
          onChange={(e) => setConnectionSecretRef(e.target.value)}
        />
      </div>
    </SkyDialog>
  );
}

function EditInstanceDialog({
  instance,
  onClose,
  onSaved,
}: {
  instance: AdminInstance;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(instance.label);
  const [status, setStatus] = useState(instance.status);
  const [connectionSecretRef, setConnectionSecretRef] = useState(
    instance.connectionSecretRef ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await updateInstance(instance.id, {
        label: label.trim(),
        status: status as "active" | "provisioning" | "disabled",
        connectionSecretRef: connectionSecretRef.trim() || null,
      });
      onSaved();
    } catch (e: unknown) {
      setErr(problemTitle(e, "Failed to update instance."));
      setBusy(false);
    }
  }

  return (
    <SkyDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Edit ${instance.name}`}
      size="md"
      primaryAction={{ label: "Save changes", onClick: submit, loading: busy, disabled: busy }}
      secondaryAction={{ label: "Cancel", onClick: onClose, disabled: busy }}
    >
      <div className="flex flex-col gap-3">
        {err && <AdminError title="Could not update instance" message={err} />}
        <SkyInput label="Label" required value={label} onChange={(e) => setLabel(e.target.value)} />
        <SkyDropdown
          label="Status"
          options={STATUS_OPTIONS}
          selectedOptions={[status]}
          onOptionSelect={(vals) =>
            setStatus((vals[0] as "active" | "provisioning" | "disabled") ?? "active")
          }
        />
        <SkyInput
          label="Connection secret reference"
          placeholder="e.g. LAKEBASE_SANDBOX_PASSWORD"
          hint={SECRET_REF_HINT}
          value={connectionSecretRef}
          onChange={(e) => setConnectionSecretRef(e.target.value)}
        />
      </div>
    </SkyDialog>
  );
}
